import React, { useContext, useEffect, useState, useMemo } from "react";
import { headerRouterTable } from "./utils"
import {
    OptionsImage,
    StatusText
} from "./style"

import details from '../../../../assets/router/details.svg';
import edit from '../../../../assets/router/edit-pen.svg'; 
import trash from '../../../../assets/router/trash.svg';
import { RouterContext, Router } from "../../../../contexts/RouterContext";
import SectionContext from "../../../../contexts/SectionContext";
import ModalContext from "../../../../contexts/ModalContext";
import axios from '../../../../config/axios';
import { useNavigate, useOutletContext } from "react-router-dom";
import { RouterOutletType } from "../index2";
import { GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from "../../../../components/styles/table-style";
import LoadMoreButton from "../../../../components/LoadMoreButton";
import { FeedbackContext } from "../../../../contexts/FeedbackContext";
import TableFiltersContext from "../../../../contexts/TableFiltersContext"; 
import { TableContainer } from "../style";

interface routerTableType {
    onChange?: () => void
}

const RouterTable: React.FC<routerTableType> = (props) => {
    // Captura o search do context global
    const { search, setTabIndex } = useOutletContext<RouterOutletType & { search: string }>();

    const dataFormatter = (isoDateString: string): string => {
        if (!isoDateString) return '';
        const dateUtc = new Date(isoDateString);
        if (isNaN(dateUtc.getTime())) return '';
        const year = dateUtc.getUTCFullYear();
        const month = dateUtc.getUTCMonth() + 1;
        const day = dateUtc.getUTCDate();
        const formattedMonth = month < 10 ? `0${month}` : month;
        const formattedDay = day < 10 ? `0${day}` : day;
        return `${formattedDay}/${formattedMonth}/${year}`;
    };

    const { setRouter } = useContext(RouterContext);
    const { setRouterSection } = useContext(SectionContext);
    const [routes, setRoutes] = useState<Router[]>([]); 
    const [totalRoutes, setTotalRoutes] = useState<number | null>(null);
    const [page, setPage] = useState(0); 
    const [hasMore, setHasMore] = useState(true);
    
    const { setFeedback } = useContext(FeedbackContext);
    const navigate = useNavigate(); 
    const { routerStatusFilter } = useContext(TableFiltersContext);

    useEffect(() => {
        setTabIndex(1);
        setRouterSection('router');
    }, []);

    const handleEdit = async (id: number) => {
        try {
            const fetchedRouter = await getRoute(id);
            setRouter(fetchedRouter);
            setRouterSection('newRouter'); 
            navigate('/panel/router/routers/edit');
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar roteiro para edição!',
                statusOk: false
            });
        }
    };
    
    const handleView = async (id: number) => {
        try {
            await getRoute(id); 
            navigate(`${id}`); 
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar detalhes do roteiro!',
                statusOk: false
            });
        }
    };

    const deleteRoute = async (id: number) => {
        try {
            await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/${id}`);
            setPage(0); 
            setHasMore(true); 
            getRoutes(0, routerStatusFilter, search); 
            setFeedback({
                isOpen: true,
                message: 'Rota deletada!',
                statusOk: true
            });
        } catch {
            setFeedback({
                isOpen: true,
                message: 'Erro ao deletar rota!',
                statusOk: false
            });
        }
    };
    
    const getRoute = async (id: number): Promise<Router> => { 
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/${id}`);
        const routeData = response.data as Router; 
        setRouter(routeData); 
        return routeData; 
    };

    

const getRoutes = async (pageNumber: number, statusFilter: string | null = null, searchTerm: string = '') => {
    try {
        const params = new URLSearchParams();
        params.append('page', pageNumber.toString());
        params.append('size', '50');

        if (statusFilter && statusFilter !== 'Todos' && statusFilter !== '') {
            params.append('status', statusFilter);
        }

        if (searchTerm && searchTerm.trim() !== '') {
            params.append('search', searchTerm);
        }

        // Requisição da lista
        const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route?${params.toString()}`;
        const response = await axios.get(url);
        const fetchedRoutes = response.data as Router[];

        // Sincronização do Contador (importante para o LoadMoreButton)
        try {
            const countParams = new URLSearchParams();
            if (statusFilter && statusFilter !== 'Todos' && statusFilter !== '') {
                countParams.append('status', statusFilter);
            }
            if (searchTerm && searchTerm.trim() !== '') {
                countParams.append('search', searchTerm);
            }

            const queryCount = countParams.toString() ? `?${countParams.toString()}` : '';
            const countUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/router/route/count${queryCount}`;
            const countResp = await axios.get(countUrl);
            
            setTotalRoutes(countResp.data?.total ?? null);
        } catch (err) {
            setTotalRoutes(null);
        }

        return fetchedRoutes;
    } catch (error) {
        console.error("Erro ao buscar rotas:", error);
        setFeedback({
            isOpen: true,
            message: 'Erro ao carregar rotas!',
            statusOk: false
        });
        return [];
    }
};

    // Efeito para busca e filtro com Debounce
    useEffect(() => {
    const fetchInitialRoutes = async () => {
        setPage(0);
        setHasMore(true);
        
        // Buscamos com os valores atuais de status e busca
        const initialRoutes = await getRoutes(0, routerStatusFilter, search);
        
        const hasMoreData = initialRoutes.length >= 50;
        const dataToShow = initialRoutes;
        
        setRoutes(dataToShow);
        setHasMore(hasMoreData);
    };

    const delayDebounceFn = setTimeout(() => {
        fetchInitialRoutes();
    }, 100);

    return () => clearTimeout(delayDebounceFn);
}, [routerStatusFilter, search]); // Monitora ambos
    const loadMoreRoutes = async () => {
        if (!hasMore) return;
        try {
            const nextPage = page + 1;
            const newRoutes = await getRoutes(nextPage, routerStatusFilter, search);
            const hasMoreData = newRoutes.length >= 50;
            const dataToShow = newRoutes;
            
            setHasMore(hasMoreData);
            setRoutes(prevRoutes => [...prevRoutes, ...dataToShow]);
            setPage(nextPage);
        } catch (error) {
            setFeedback({
                isOpen: true,
                message: 'Erro ao carregar mais rotas',
                statusOk: false
            });
        }
    };

    const mapStatus = (status: string): string => {
        if (status === 'A programar' || status === 'Rota programada') return 'pending'; 
        if (status === 'Rota cancelado') return 'cancel';
        if (status === 'Rota concluida' ) return 'completed';
        return '';
    };

    const filteredRoutes = useMemo(() => routes, [routes]); 

    return (
        <>
            <TableContainer>
                <GlobalTable>
                    <thead>
                        <HeaderLine>
                            {headerRouterTable.map((header) => (
                                <Th key={header}>{header}</Th>
                            ))}
                            <Th></Th>
                        </HeaderLine>
                    </thead>
                    <tbody>
                        {filteredRoutes.length > 0 ? (
                            filteredRoutes.map((routerItem: Router) => (
                                <Tr key={routerItem.id}>
                                    <Td>{routerItem.id}</Td>
                                    <Td>{routerItem.origin}</Td>
                                    <Td>
                                        <StatusText className={mapStatus(routerItem.status)}>
                                            {routerItem.status}
                                        </StatusText>
                                    </Td>
                                    <Td>{routerItem.Vehicle?.plate}</Td> 
                                    <Td>{dataFormatter(routerItem.date)}</Td>
                                    <Td>{routerItem.track ? routerItem.track : "-"}</Td>
                                    <TdButtons>
                                        <OptionsImage onClick={() => handleView(routerItem.id!)} src={details} alt='Icon to show router' />
                                        <OptionsImage onClick={() => handleEdit(routerItem.id!)} src={edit} alt='Icon to edit router' />
                                        <OptionsImage onClick={() => deleteRoute(routerItem.id!)} src={trash} alt='Icon to delet router' />
                                    </TdButtons>
                                </Tr>
                            ))
                        ) : (
                            <Tr>
                                <Td colSpan={headerRouterTable.length + 1} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                    {search ? `Nenhuma rota encontrada para "${search}"` : 'Nenhuma rota cadastrada.'}
                                </Td>
                            </Tr>
                        )}
                    </tbody>
                </GlobalTable>
                <LoadMoreButton
                    currentCount={routes.length}
                    onLoadMore={loadMoreRoutes}
                    hasMore={hasMore}
                    totalCount={totalRoutes}
                />
            </TableContainer>
        </>
    );
}

export default RouterTable;